import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { getAccessToken } from "../awsAuth";

// Single shared source for Header.js + Sidebar.js, which both render at the
// same time on every non-auth/login/settings route and used to each run
// their own independent 20s poll of the same two endpoints — 4 requests
// every ~20s per logged-in user instead of 2. One poll loop here, shared by
// both consumers via context.
const UNREAD_POLL_MS = 20000;

const MSG_MARK_KEY = "cvanto:messages:mark";
const NOTIF_MARK_KEY = "cvanto:notifs:mark";

// localStorage write + custom window event: cross-tab (storage event fires
// in *other* tabs) and same-tab producers that don't share this context
// (Messages.js marks a thread read via its own copy of this same key).
function broadcastMessagesClear() {
  try {
    localStorage.setItem(MSG_MARK_KEY, String(Date.now()));
  } catch {}
  try {
    window.dispatchEvent(new Event(MSG_MARK_KEY));
  } catch {}
}

function broadcastNotifClear() {
  try {
    localStorage.setItem(NOTIF_MARK_KEY, String(Date.now()));
  } catch {}
  try {
    window.dispatchEvent(new Event(NOTIF_MARK_KEY));
  } catch {}
}

const UnreadCountsContext = createContext({
  messagesUnread: 0,
  notifUnread: 0,
  markMessagesReadLocally: () => {},
  markNotificationsReadLocally: async () => {},
});

export const UnreadCountsProvider = ({ children }) => {
  const { isLoggedIn, userId } = useAuth();

  const [messagesUnread, setMessagesUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);

  const markNotifDebounceRef = useRef(0);

  useEffect(() => {
    if (!isLoggedIn || !userId) {
      setMessagesUnread(0);
      setNotifUnread(0);
      return;
    }

    let cancelled = false;
    let inFlight = false;
    let timeoutId = null;

    const loadUnread = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;

      try {
        const token = await getAccessToken();

        if (!token) {
          if (!cancelled) {
            setMessagesUnread(0);
            setNotifUnread(0);
          }
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };

        const [messagesRes, notifRes] = await Promise.all([
          axios.get("/api/messages/unread-count", { headers }).catch((e) => {
            console.debug("UnreadCounts: messages unread fetch error", e);
            return null;
          }),
          axios.get("/api/notifications/unread-count", { headers }).catch((e) => {
            console.debug("UnreadCounts: notifications unread fetch error", e);
            return null;
          }),
        ]);

        if (cancelled) return;

        if (messagesRes) {
          const n = Number(messagesRes.data?.count || 0);
          setMessagesUnread(Number.isFinite(n) ? n : 0);
        }

        if (notifRes) {
          const n = Number(notifRes.data?.count || 0);
          setNotifUnread(Number.isFinite(n) ? n : 0);
        }
      } finally {
        inFlight = false;
      }
    };

    const startLoop = async () => {
      await loadUnread();

      if (!cancelled) {
        timeoutId = window.setTimeout(startLoop, UNREAD_POLL_MS);
      }
    };

    startLoop();

    const onFocus = () => loadUnread();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener("focus", onFocus);
    };
  }, [isLoggedIn, userId]);

  // Cross-tab (storage) + same-tab external producers (Messages.js) that
  // signal "already read" without going through this context.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === NOTIF_MARK_KEY) setNotifUnread(0);
      if (e.key === MSG_MARK_KEY) setMessagesUnread(0);
    };
    const onNotifLocal = () => setNotifUnread(0);
    const onMsgLocal = () => setMessagesUnread(0);

    window.addEventListener("storage", onStorage);
    window.addEventListener(NOTIF_MARK_KEY, onNotifLocal);
    window.addEventListener(MSG_MARK_KEY, onMsgLocal);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(NOTIF_MARK_KEY, onNotifLocal);
      window.removeEventListener(MSG_MARK_KEY, onMsgLocal);
    };
  }, []);

  const markMessagesReadLocally = useCallback(() => {
    setMessagesUnread(0);
    broadcastMessagesClear();
  }, []);

  const markNotificationsReadLocally = useCallback(async () => {
    setNotifUnread(0);
    broadcastNotifClear();

    if (!isLoggedIn || !userId) return;

    const now = Date.now();
    if (now - markNotifDebounceRef.current < 1500) return;
    markNotifDebounceRef.current = now;

    try {
      const token = await getAccessToken();
      if (!token) return;

      await axios.post(
        "/api/notifications/mark-all-read",
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch {}
  }, [isLoggedIn, userId]);

  const value = useMemo(
    () => ({
      messagesUnread,
      notifUnread,
      markMessagesReadLocally,
      markNotificationsReadLocally,
    }),
    [messagesUnread, notifUnread, markMessagesReadLocally, markNotificationsReadLocally],
  );

  return (
    <UnreadCountsContext.Provider value={value}>
      {children}
    </UnreadCountsContext.Provider>
  );
};

export const useUnreadCounts = () => useContext(UnreadCountsContext);
